import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import client from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { Store, Wallet, ShoppingBag, Users, TrendingUp, ExternalLink, CheckCircle2, Clock } from "lucide-react";

export default function SuperAdmin() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const [stats, setStats] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [orders, setOrders] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [accountingFor, setAccountingFor] = useState(null);
  const [accountingData, setAccountingData] = useState(null);
  const [accountingLoading, setAccountingLoading] = useState(false);
  const [billingFor, setBillingFor] = useState(null);

  const load = async () => {
    const [s, r, o, a] = await Promise.all([
      client.get("/admin/stats"),
      client.get("/admin/restaurants"),
      client.get("/admin/orders"),
      client.get("/admin/analytics?days=30"),
    ]);
    setStats(s.data); setRestaurants(r.data); setOrders(o.data); setAnalytics(a.data);
  };

  useEffect(() => { if (user?.role === "super_admin") load(); }, [user]);

  if (loading) return <div className="p-10 text-center text-zinc-500">...</div>;
  if (!user) return <Navigate to="/sa-login" replace />;
  if (user.role !== "super_admin") return <Navigate to="/dashboard" replace />;

  const toggle = async (rid, active) => { await client.put(`/admin/restaurants/${rid}/toggle`, { active }); load(); };

  const setThreshold = async (rid, val) => {
    const n = parseFloat(val || 0);
    if (isNaN(n) || n < 0) return;
    try {
      await client.put(`/admin/restaurants/${rid}/commission-threshold`, { threshold: n });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  const suspend = async (rid, suspended) => {
    let reason = "";
    if (suspended) {
      reason = window.prompt(t.admin.suspendReasonPrompt || "Motivo della sospensione (opzionale):", "") || "";
    }
    try {
      await client.put(`/admin/restaurants/${rid}/suspend`, { suspended, reason });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Error"); }
  };

  const hardDelete = async (rid, name) => {
    const phrase = window.prompt((t.admin.deleteConfirmPrompt || "Per cancellare DEFINITIVAMENTE il ristorante {n} digita CANCELLA").replace("{n}", name), "");
    if ((phrase || "").trim().toUpperCase() !== "CANCELLA") return;
    try {
      await client.delete(`/admin/restaurants/${rid}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Error"); }
  };

  const giftFree = async (rid, name, currentlyEnabled) => {
    const msg = currentlyEnabled
      ? (t.admin.giftFreeRevoke || "Revocare il piano gratuito a {n}? L'abbonamento tornerà 'inactive'.").replace("{n}", name)
      : (t.admin.giftFreeConfirm || "Regalare tutto gratis a {n}?\n- Abbonamento attivo per 10 anni\n- Commissioni esistenti saldate\n- Sblocco automatico\n- Nessuna sospensione futura").replace("{n}", name);
    if (!window.confirm(msg)) return;
    try {
      await client.put(`/admin/restaurants/${rid}/gift-free`, { enabled: !currentlyEnabled });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Error"); }
  };

  const openAccounting = async (r) => {
    setAccountingFor(r);
    setAccountingData(null);
    setAccountingLoading(true);
    try {
      const { data } = await client.get(`/admin/restaurants/${r.id}/accounting`);
      setAccountingData(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally {
      setAccountingLoading(false);
    }
  };
  const closeAccounting = () => { setAccountingFor(null); setAccountingData(null); };

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 py-10 w-full flex-1">
        <div className="mb-8">
          <div className="chip chip-yellow inline-flex">RENOVA 360</div>
          <h1 className="font-heading text-4xl tracking-tight mt-3">{t.admin.title}</h1>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-kpis">
            <Kpi icon={<Store className="w-5 h-5" />} label={t.admin.kpiRestaurants} value={stats.active_restaurants} color="cyan" />
            <Kpi icon={<Wallet className="w-5 h-5" />} label={t.admin.kpiCommission} value={`€ ${stats.commission_renova_eur.toFixed(2)}`} color="yellow" highlight />
            <Kpi icon={<CheckCircle2 className="w-5 h-5" />} label={t.admin.kpiCommissionCollected || "Incassato"} value={`€ ${(stats.commission_collected_eur || 0).toFixed(2)}`} color="cyan" />
            <Kpi icon={<Clock className="w-5 h-5" />} label={t.admin.kpiCommissionToCollect || "Da incassare"} value={`€ ${(stats.commission_to_collect_eur || 0).toFixed(2)}`} color="magenta" />
            <Kpi icon={<TrendingUp className="w-5 h-5" />} label={t.admin.kpiSubs} value={`€ ${stats.subscriptions_revenue_eur.toFixed(2)}`} color="violet" />
            <Kpi icon={<ShoppingBag className="w-5 h-5" />} label={t.admin.kpiGmv} value={`€ ${stats.total_gmv_eur.toFixed(2)}`} color="cyan" />
            <Kpi icon={<Users className="w-5 h-5" />} label={t.admin.kpiActiveSubs} value={stats.active_subscriptions} color="violet" />
            <Kpi icon={<ShoppingBag className="w-5 h-5" />} label={t.admin.kpiOrders} value={stats.total_orders} color="magenta" />
          </div>
        )}

        {analytics && <AnalyticsChart data={analytics} t={t} />}

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 card-neon p-6">
            <h2 className="font-heading font-semibold text-xl mb-5">{t.admin.list}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-zinc-500 border-b border-line font-mono">
                    <th className="py-3 pr-3">Nome</th>
                    <th className="py-3 pr-3">P.IVA</th>
                    <th className="py-3 pr-3">Owner</th>
                    <th className="py-3 pr-3">Plan</th>
                    <th className="py-3 pr-3">Status</th>
                    <th className="py-3 pr-3"></th>
                    <th className="py-3 pr-3"></th>
                    <th className="py-3 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {restaurants.map((r) => (
                    <tr key={r.id} className="border-b border-line last:border-0" data-testid={`admin-rest-${r.id}`}>
                      <td className="py-3 pr-3 font-semibold text-zinc-200">{r.name}</td>
                      <td className="py-3 pr-3 font-mono text-xs text-zinc-400">{r.owner_vat}</td>
                      <td className="py-3 pr-3 text-zinc-400 text-xs">{r.owner_email}</td>
                      <td className="py-3 pr-3 uppercase text-xs text-neon-cyan font-mono">{r.owner_plan}</td>
                      <td className="py-3 pr-3">
                        <span className={`chip !text-[10px] ${r.owner_sub_status === "active" ? "chip-cyan" : r.owner_sub_status === "trial" ? "chip-yellow" : ""}`}>{r.owner_sub_status}</span>
                        {r.admin_suspended && (
                          <span className="ml-1 chip chip-yellow !text-[9px]" data-testid={`admin-suspended-pill-${r.id}`}>{t.admin.suspended || "Sospeso"}</span>
                        )}
                        {r.debt_auto_suspended && (
                          <span className="ml-1 chip !text-[9px] text-red-300 border-red-500/40 bg-red-500/10" data-testid={`debt-suspended-pill-${r.id}`}>{t.admin.debtSuspended || "Sospeso debito"}</span>
                        )}
                        {!r.active && (
                          <span className="ml-1 chip !text-[9px] text-red-300 border-red-500/40 bg-red-500/10" data-testid={`admin-cancelled-pill-${r.id}`}>{t.admin.cancelled || "Annullato"}</span>
                        )}
                        <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono text-zinc-500">
                          <span>{t.admin.thresholdLabel || "Soglia €"}</span>
                          <input
                            type="number"
                            min={0}
                            step={5}
                            defaultValue={r.commission_threshold ?? 50}
                            onBlur={(e) => setThreshold(r.id, e.target.value)}
                            className="w-16 bg-deep-card border border-line rounded px-1.5 py-0.5 text-zinc-200"
                            data-testid={`admin-threshold-${r.id}`}
                          />
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            onClick={() => suspend(r.id, !r.admin_suspended)}
                            className="text-[10px] font-semibold underline text-yellow-300"
                            data-testid={`admin-suspend-${r.id}`}
                          >
                            {r.admin_suspended ? (t.admin.unsuspend || "Riattiva") : (t.admin.suspend || "Sospendi")}
                          </button>
                          <button
                            onClick={() => toggle(r.id, !r.active)}
                            className="text-[10px] font-semibold underline text-zinc-300"
                            data-testid={`admin-toggle-${r.id}`}
                          >
                            {r.active ? (t.admin.cancel || "Annulla") : (t.admin.restore || "Ripristina")}
                          </button>
                          <button
                            onClick={() => hardDelete(r.id, r.name)}
                            className="text-[10px] font-semibold underline text-red-400"
                            data-testid={`admin-delete-${r.id}`}
                          >
                            {t.admin.delete || "Cancella"}
                          </button>
                          <button
                            onClick={() => openAccounting(r)}
                            className="text-[10px] font-semibold underline text-neon-cyan"
                            data-testid={`admin-accounting-${r.id}`}
                          >
                            {t.admin.accounting || "Contabilità"}
                          </button>
                          <button
                            onClick={() => setBillingFor(r)}
                            className={`text-[10px] font-semibold underline ${(r.owner_billing && r.owner_billing.company_name) ? "text-zinc-300" : "text-amber-300"}`}
                            data-testid={`admin-billing-${r.id}`}
                            title={(r.owner_billing && r.owner_billing.company_name) ? "Dati fatturazione disponibili" : "Dati fatturazione mancanti"}
                          >
                            🧾 {t.admin.billing || "Fatturazione"}
                          </button>
                          <button
                            onClick={() => giftFree(r.id, r.name, r.owner_gifted_free)}
                            className={`text-[10px] font-semibold underline ${r.owner_gifted_free ? "text-zinc-300" : "text-pink-300"}`}
                            data-testid={`admin-gift-free-${r.id}`}
                            title={r.owner_gifted_free ? (t.admin.giftFreeActiveTitle || "Piano gratuito attivo — click per revocare") : (t.admin.giftFreeTitle || "Regala tutto gratis al ristoratore")}
                          >
                            {r.owner_gifted_free ? `🎁 ${t.admin.giftFreeRevokeShort || "Revoca free"}` : `🎁 ${t.admin.giftFreeShort || "Regala tutto free"}`}
                          </button>
                        </div>
                      </td>
                      <td className="py-3 pr-3">
                        <a target="_blank" rel="noreferrer" href={`/r/${r.slug}`} className="text-[10px] inline-flex items-center gap-1 text-neon-cyan"><ExternalLink className="w-3 h-3" /> {t.admin.open}</a>
                      </td>
                      <td className="py-3 pr-3">
                        {r.owner_gifted_free && (
                          <span className="chip !text-[9px] chip-violet" data-testid={`admin-gifted-pill-${r.id}`}>🎁 FREE</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="lg:col-span-5 card-neon p-6">
            <h2 className="font-heading font-semibold text-xl mb-5">{t.admin.orders}</h2>
            <div className="space-y-3 max-h-[520px] overflow-auto scrollbar-thin">
              {orders.length === 0 && <p className="text-zinc-500 text-sm">—</p>}
              {orders.map((o) => (
                <div key={o.id} className="border border-line bg-deep-card rounded-xl p-3 text-sm" data-testid={`admin-order-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-zinc-200">{o.customer_name}</span>
                    <span className="font-heading font-bold text-neon-yellow">€ {Number(o.total).toFixed(2)}</span>
                  </div>
                  <div className="text-xs text-zinc-500 font-mono">{o.restaurant_name || o.restaurant_id} · {new Date(o.created_at).toLocaleString()}</div>
                  <div className="text-xs mt-1">
                    <span className={`chip !text-[10px] ${o.payment_status === "paid" ? "chip-cyan" : ""}`}>{o.payment_status}</span>
                    <span className="ml-2 text-zinc-400 font-mono">6% = € {Number(o.commission_renova).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {accountingFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeAccounting} data-testid="accounting-modal">
          <div className="bg-deep-card border border-line w-full max-w-3xl rounded-3xl p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{t.admin.accounting || "Contabilità"}</div>
                <h3 className="font-heading font-bold text-2xl mt-1">{accountingFor.name}</h3>
                <div className="text-xs text-zinc-400 mt-1 font-mono">
                  {accountingFor.owner_email} · P.IVA {accountingFor.owner_vat} · {accountingFor.owner_plan}
                </div>
              </div>
              <button onClick={closeAccounting} className="text-zinc-400 hover:text-white" data-testid="accounting-close">✕</button>
            </div>
            {accountingLoading && <div className="text-center text-zinc-500 py-10">…</div>}
            {accountingData && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                  <KpiSmall label={t.admin.gmv || "GMV"} value={`€ ${accountingData.totals.gmv.toFixed(2)}`} testId="acc-kpi-gmv" />
                  <KpiSmall label={t.admin.commissionTotal || "Commissione 6%"} value={`€ ${accountingData.totals.commission_total.toFixed(2)}`} testId="acc-kpi-commission" />
                  <KpiSmall label={t.admin.commissionSettled || "Saldata"} value={`€ ${accountingData.totals.commission_settled.toFixed(2)}`} color="cyan" testId="acc-kpi-settled" />
                  <KpiSmall label={t.admin.commissionPending || "Da saldare cash"} value={`€ ${accountingData.totals.commission_pending_cash.toFixed(2)}`} color="yellow" testId="acc-kpi-pending" />
                  <KpiSmall label={t.admin.subscriptions || "Abbonamenti"} value={`€ ${accountingData.totals.subscription_paid.toFixed(2)}`} testId="acc-kpi-subs" />
                  <KpiSmall label={t.admin.extraResto || "Extra rist."} value={`€ ${accountingData.totals.extra_paid.toFixed(2)}`} testId="acc-kpi-extra" />
                  <KpiSmall label={t.admin.orders || "Ordini"} value={accountingData.totals.orders_count} testId="acc-kpi-orders" />
                  <KpiSmall label={t.admin.revenueRenova || "Ricavi Renova"} value={`€ ${accountingData.totals.renova_revenue.toFixed(2)}`} color="violet" highlight testId="acc-kpi-revenue" />
                </div>
                <div className="mt-6">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-2">{t.admin.ledger || "Movimenti contabili"}</div>
                  <div className="space-y-1.5 max-h-[40vh] overflow-auto scrollbar-thin pr-2">
                    {accountingData.ledger.length === 0 ? (
                      <div className="text-zinc-500 text-sm py-6 text-center">{t.admin.noLedger || "Nessun movimento"}</div>
                    ) : accountingData.ledger.map((e, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 p-2 rounded-lg border border-line bg-deep/40 text-xs" data-testid={`ledger-row-${i}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-zinc-300 truncate">{e.label}</div>
                          <div className="text-zinc-500 font-mono text-[10px]">{e.ts ? new Date(e.ts).toLocaleString() : ""}</div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-neon-cyan">€ {Number(e.amount || 0).toFixed(2)}</div>
                          {e.kind === "order" && e.commission > 0 && (
                            <div className="text-[10px] text-zinc-400 font-mono">
                              6% € {Number(e.commission).toFixed(2)} {e.commission_settled ? "✓" : "·"}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {billingFor && (
        <BillingModal rest={billingFor} onClose={() => setBillingFor(null)} t={t} />
      )}
      <Footer />
    </div>
  );
}

function BillingModal({ rest, onClose, t }) {
  const b = rest?.owner_billing || {};
  const present = !!b.company_name;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-billing-modal">
      <div className="bg-deep-card border border-line w-full max-w-2xl rounded-3xl p-6 max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{t.admin.billing || "FATTURAZIONE"}</div>
            <h3 className="font-heading font-bold text-2xl mt-1">{rest.name}</h3>
            <div className="text-xs text-zinc-400 mt-1 font-mono">{rest.owner_email}</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="admin-billing-close">✕</button>
        </div>
        {!present ? (
          <div className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/5 p-5 text-sm text-amber-200" data-testid="admin-billing-empty">
            {t.admin.billingEmpty || "Il ristoratore non ha ancora inserito i dati di fatturazione. Non potrà attivare un piano a pagamento finché non li compila."}
          </div>
        ) : (
          <dl className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm" data-testid="admin-billing-grid">
            <BillingField label={t.admin.bCompany || "Ragione sociale / Nome"} value={b.company_name} testid="bf-company" />
            <BillingField label={t.admin.bVat || "P.IVA / Codice Fiscale"} value={b.vat_or_fiscal_code} testid="bf-vat" />
            <BillingField label={t.admin.bPhone || "Telefono"} value={b.phone} testid="bf-phone" />
            <BillingField label={t.admin.bEmail || "Email fatturazione"} value={b.email} testid="bf-email" />
            <BillingField label={t.admin.bAddress || "Indirizzo"} value={b.address} testid="bf-address" cls="sm:col-span-2" />
            <BillingField label={t.admin.bPec || "PEC / SDI"} value={b.pec_or_sdi} testid="bf-pec" />
          </dl>
        )}
        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="btn-outline !py-2 !px-4 text-xs" data-testid="admin-billing-close-btn">{t.admin.close || "Chiudi"}</button>
        </div>
      </div>
    </div>
  );
}

function BillingField({ label, value, testid, cls = "" }) {
  return (
    <div className={`py-1.5 border-b border-line/40 ${cls}`} data-testid={testid}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono mb-0.5">{label}</dt>
      <dd className="text-sm text-zinc-100 break-all">{value || <span className="text-zinc-600">—</span>}</dd>
    </div>
  );
}

function KpiSmall({ label, value, color = "neutral", highlight, testId }) {
  const cls = color === "cyan" ? "text-neon-cyan" : color === "yellow" ? "text-yellow-300" : color === "violet" ? "text-neon-violet" : "text-zinc-100";
  return (
    <div className={`p-3 rounded-xl border ${highlight ? "border-neon-violet/50 bg-neon-violet/5" : "border-line bg-deep/40"}`} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{label}</div>
      <div className={`font-heading font-bold text-lg mt-0.5 ${cls}`}>{value}</div>
    </div>
  );
}

function Kpi({ icon, label, value, color = "cyan", highlight }) {
  const bg = color === "cyan" ? "bg-[#00E5FF] text-[#06061a]" : color === "violet" ? "bg-[#7C3AED] text-white" : color === "yellow" ? "bg-[#FFD600] text-[#06061a]" : "bg-[#FF2EC8] text-white";
  return (
    <div className={`card-neon p-5 ${highlight ? "glow-yellow" : ""}`} data-testid={`kpi-${label}`}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{label}</div>
      <div className="mt-1 flex items-center justify-between">
        <div className={`font-heading font-black text-3xl ${highlight ? "text-neon-yellow text-glow-yellow" : ""}`}>{value}</div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bg}`}>{icon}</div>
      </div>
    </div>
  );
}

function AnalyticsChart({ data, t }) {
  const series = data.series || [];
  const maxGmv = Math.max(1, ...series.map((s) => s.gmv || 0));
  const W = 720;
  const H = 160;
  const padL = 36;
  const padR = 10;
  const padT = 10;
  const padB = 20;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const stepX = series.length > 1 ? innerW / (series.length - 1) : innerW;

  const pointsGmv = series.map((s, i) => `${padL + i * stepX},${padT + innerH - (innerH * (s.gmv || 0)) / maxGmv}`).join(" ");
  const pointsCom = series.map((s, i) => `${padL + i * stepX},${padT + innerH - (innerH * ((s.commission || 0) * (maxGmv / Math.max(1, Math.max(...series.map((x) => x.commission || 0)))))) / maxGmv}`).join(" ");

  const firstLabel = series.length > 0 ? series[0].date.slice(5) : "";
  const lastLabel = series.length > 0 ? series[series.length - 1].date.slice(5) : "";

  return (
    <div className="card-neon p-6 mb-10" data-testid="admin-analytics-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-heading font-semibold text-xl">{(t.dashboard?.analyticsTitle || "Analytics last {d} days").replace("{d}", String(data.days || 30))}</h2>
        <div className="flex gap-4 text-xs font-mono">
          <span className="text-neon-cyan">● {t.dashboard?.gmv || "GMV"} € {data.totals.gmv.toFixed(2)}</span>
          <span className="text-neon-yellow">● {t.dashboard?.commission || "Commission"} € {data.totals.commission.toFixed(2)}</span>
          <span className="text-zinc-400">{t.dashboard?.ordersCount || "Orders"}: {data.totals.orders}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-full" style={{ minWidth: 480 }} data-testid="admin-chart-svg">
          <defs>
            <linearGradient id="grdCyan" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#00E5FF" stopOpacity="0" />
            </linearGradient>
          </defs>
          {/* baseline */}
          <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="#1f2937" strokeWidth="1" />
          {/* GMV area */}
          {series.length > 1 && (
            <polygon points={`${padL},${padT + innerH} ${pointsGmv} ${W - padR},${padT + innerH}`} fill="url(#grdCyan)" />
          )}
          {/* GMV line */}
          <polyline points={pointsGmv} fill="none" stroke="#00E5FF" strokeWidth="2" />
          {/* Commission line */}
          <polyline points={pointsCom} fill="none" stroke="#FFD600" strokeWidth="2" strokeDasharray="4 4" />
          {/* labels */}
          <text x={padL} y={H - 4} fontSize="10" fill="#71717a" fontFamily="ui-monospace">{firstLabel}</text>
          <text x={W - padR} y={H - 4} fontSize="10" fill="#71717a" fontFamily="ui-monospace" textAnchor="end">{lastLabel}</text>
          <text x={padL - 4} y={padT + 8} fontSize="10" fill="#71717a" fontFamily="ui-monospace" textAnchor="end">€ {maxGmv.toFixed(0)}</text>
          <text x={padL - 4} y={padT + innerH} fontSize="10" fill="#71717a" fontFamily="ui-monospace" textAnchor="end">0</text>
        </svg>
      </div>
      {data.top_restaurants && data.top_restaurants.length > 0 && (
        <div className="mt-6">
          <h3 className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-2">{t.dashboard?.topRestaurants || "Top restaurants"}</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="admin-top-restaurants">
            {data.top_restaurants.slice(0, 6).map((r) => (
              <div key={r.restaurant_id} className="flex items-center justify-between gap-2 text-xs border border-line rounded-lg px-3 py-2">
                <span className="truncate">{r.name || r.restaurant_id}</span>
                <span className="font-mono text-neon-cyan">€ {Number(r.gmv).toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
